import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './Typography.module.css';

/**
 * Typography — heading / body / inline text components, reimplemented in
 * Athena's idiom (JSX + CSS Module + 2.0 tokens). shadcn ships no typography
 * styles (it documents utility classes); here each example is a small component
 * that renders the right element with a flat, mode-invariant 2.0 class.
 *
 * Heading sizes are capped to the existing --ds2-font-size scale (no new
 * tokens). Inline code reuses var(--font-family) (no monospace font), set apart
 * by a --muted background + --border. Preview-only for now (globals.css isn't
 * imported into the app).
 */

const TypographyH1 = ({ className = '', children, ...props }) => (
  <h1 className={`${styles.h1} ${className}`.trim()} {...props}>{children}</h1>
);

const TypographyH2 = ({ className = '', children, ...props }) => (
  <h2 className={`${styles.h2} ${className}`.trim()} {...props}>{children}</h2>
);

const TypographyH3 = ({ className = '', children, ...props }) => (
  <h3 className={`${styles.h3} ${className}`.trim()} {...props}>{children}</h3>
);

const TypographyH4 = ({ className = '', children, ...props }) => (
  <h4 className={`${styles.h4} ${className}`.trim()} {...props}>{children}</h4>
);

const TypographyP = ({ className = '', children, ...props }) => (
  <p className={`${styles.p} ${className}`.trim()} {...props}>{children}</p>
);

const TypographyBlockquote = ({ className = '', children, ...props }) => (
  <blockquote className={`${styles.blockquote} ${className}`.trim()} {...props}>{children}</blockquote>
);

const TypographyList = ({ className = '', children, ...props }) => (
  <ul className={`${styles.list} ${className}`.trim()} {...props}>{children}</ul>
);

// Renders a bare <table>; the consumer supplies <thead>/<tbody>. Bordered cells
// + zebra rows come from the module's descendant selectors.
const TypographyTable = ({ className = '', children, ...props }) => (
  <table className={`${styles.table} ${className}`.trim()} {...props}>{children}</table>
);

const TypographyInlineCode = ({ className = '', children, ...props }) => (
  <code className={`${styles.inlineCode} ${className}`.trim()} {...props}>{children}</code>
);

const TypographyLead = ({ className = '', children, ...props }) => (
  <p className={`${styles.lead} ${className}`.trim()} {...props}>{children}</p>
);

const TypographyLarge = ({ className = '', children, ...props }) => (
  <div className={`${styles.large} ${className}`.trim()} {...props}>{children}</div>
);

const TypographySmall = ({ className = '', children, ...props }) => (
  <small className={`${styles.small} ${className}`.trim()} {...props}>{children}</small>
);

const TypographyMuted = ({ className = '', children, ...props }) => (
  <p className={`${styles.muted} ${className}`.trim()} {...props}>{children}</p>
);

const typographyPropTypes = { className: PropTypes.string, children: PropTypes.node };
TypographyH1.propTypes = typographyPropTypes;
TypographyH2.propTypes = typographyPropTypes;
TypographyH3.propTypes = typographyPropTypes;
TypographyH4.propTypes = typographyPropTypes;
TypographyP.propTypes = typographyPropTypes;
TypographyBlockquote.propTypes = typographyPropTypes;
TypographyList.propTypes = typographyPropTypes;
TypographyTable.propTypes = typographyPropTypes;
TypographyInlineCode.propTypes = typographyPropTypes;
TypographyLead.propTypes = typographyPropTypes;
TypographyLarge.propTypes = typographyPropTypes;
TypographySmall.propTypes = typographyPropTypes;
TypographyMuted.propTypes = typographyPropTypes;

export {
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
  TypographyBlockquote,
  TypographyList,
  TypographyTable,
  TypographyInlineCode,
  TypographyLead,
  TypographyLarge,
  TypographySmall,
  TypographyMuted,
};
